/**
 * Unified element type system for ExcaliPlan
 * Supports both primitive shapes (rectangles, circles, arrows, text)
 * and task-aware elements (tasks, notes, milestones) with time tracking
 */

// Base properties shared by all elements
export interface BaseElement {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

// Primitive shape elements (no time tracking or hierarchy)
export interface RectangleElement extends BaseElement {
  type: 'rectangle';
  fill: string;
  stroke: string;
  strokeWidth: number;
  text?: string;
}

export interface CircleElement extends BaseElement {
  type: 'circle';
  fill: string;
  stroke: string;
  strokeWidth: number;
  text?: string;
}

export interface LineElement extends BaseElement {
  type: 'line';
  stroke: string;
  strokeWidth: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

export interface ArrowElement extends BaseElement {
  type: 'arrow';
  stroke: string;
  strokeWidth: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  arrowHeadSize: number;
}

export interface TextBoxElement extends BaseElement {
  type: 'textbox';
  text: string;
  fontSize: number;
  fontFamily: string;
  color: string;
}

// Task-aware elements (with time tracking and hierarchy)
export interface TaskElement extends BaseElement {
  type: 'task';
  text: string;
  timeEstimate: string; // e.g., "2h", "3d", "1w"
  parentId: string | null;
  children: string[];
}

export interface NoteElement extends BaseElement {
  type: 'note';
  text: string;
  timeEstimate: string;
  parentId: string | null;
  children: string[];
}

export interface MilestoneElement extends BaseElement {
  type: 'milestone';
  text: string;
  timeEstimate: string;
  parentId: string | null;
  children: string[];
}

// Union type for primitive shapes
export type PrimitiveElement =
  | RectangleElement
  | CircleElement
  | LineElement
  | ArrowElement
  | TextBoxElement;

// Union type for task-aware elements
export type TaskAwareElement = TaskElement | NoteElement | MilestoneElement;

// Main discriminated union for all element types
export type CanvasElement = PrimitiveElement | TaskAwareElement;

// Type guards
export function isPrimitiveElement(element: CanvasElement): element is PrimitiveElement {
  return ['rectangle', 'circle', 'line', 'arrow', 'textbox'].includes(element.type);
}

export function isTaskAwareElement(element: CanvasElement): element is TaskAwareElement {
  return ['task', 'note', 'milestone'].includes(element.type);
}

export function isTaskElement(element: CanvasElement): element is TaskElement {
  return element.type === 'task';
}

export function isNoteElement(element: CanvasElement): element is NoteElement {
  return element.type === 'note';
}

export function isMilestoneElement(element: CanvasElement): element is MilestoneElement {
  return element.type === 'milestone';
}

export function hasChildren(element: CanvasElement): element is TaskAwareElement {
  return isTaskAwareElement(element);
}

// Default dimensions for different element types
export const DEFAULT_DIMENSIONS = {
  task: { width: 280, height: 100 },
  note: { width: 280, height: 100 },
  milestone: { width: 280, height: 100 },
  text: { width: 200, height: 40 },
  rectangle: { width: 200, height: 150 },
  circle: { width: 120, height: 120 },
  diamond: { width: 120, height: 120 },
  line: { width: 200, height: 2 },
  arrow: { width: 200, height: 2 },
  textbox: { width: 200, height: 40 },
} as const;

// Resize constraints
export const RESIZE_CONSTRAINTS = {
  minWidth: 80,
  maxWidth: 500,
  minHeight: 40,
  maxHeight: 400,
} as const;
